import { EditOutlined } from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TLinkApi from "services/tlink.api";

const ClientConfig = ({
  allClients,
  setIsLoading,
  setSelectedClient,
  selectedClient,
}) => {
  const { grey, secondary, primary, light } = colors;
  const { id } = useParams();
  const [addData, setAddData] = useState(true);
  const [isEdit, setIsEdit] = useState({
    disabled: false,
    edit: false,
  });
  const [clientConfig, setClientConfig] = useState([]);
  const [search, setSearch] = useState("");
  let { enqueueSnackbar } = useSnackbar();

  const GetClientConfig = async () => {
    setIsLoading(true);
    try {
      let data = await TLinkApi.get(`vendors/${id}/clients`);
      if (data.data && data.data.length > 0) {
        setClientConfig(data.data.map((val) => Number(val.client_id)));
        setAddData(false);
        setIsEdit({
          disabled: true,
          edit: false,
        });
      } else {
        setAddData(true);
        setIsEdit({
          disabled: false,
          edit: false,
        });
      }

      setIsLoading(false);
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e?.response?.data?.message || "", {
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      GetClientConfig();
    }
  }, [id]);

  const save = async () => {
    setIsLoading(true);
    try {
      let payload = {
        clients: clientConfig.map((client) => {
          return {
            clientId: client,
            minVcpi: 0,
          };
        }),
      };
      await TLinkApi.put(`vendors/${id}/clients`, payload);
      await GetClientConfig();
      enqueueSnackbar("Successfully saved", {
        variant: "success",
      });
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      enqueueSnackbar(e?.response?.data?.message || "", {
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit.edit || addData) {
      setSelectedClient(null);
    }
  }, [isEdit, addData]);

  return (
    <MDBox
      py={2}
      sx={{
        background: "white",
        borderRadius: "1rem",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pb="0.5rem"
        px={2}
        sx={{ borderBottom: `1px solid ${grey[400]}`, zIndex: "2" }}
      >
        <Typography variant="v6" fontWeight="600">
          Clients
        </Typography>
        {isEdit.disabled ? (
          <MDButton
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<EditOutlined />}
            onClick={() => {
              setSelectedClient(null);
              setIsEdit({
                disabled: false,
                edit: true,
              });
            }}
          >
            Edit
          </MDButton>
        ) : (
          <Stack direction="row" alignItems="center" spacing={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => {
                if (addData) {
                  setClientConfig([]);
                } else {
                  GetClientConfig();
                }
              }}
            >
              &nbsp;cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => {
                save();
              }}
            >
              Save
            </MDButton>
          </Stack>
        )}
      </Stack>
      <Stack sx={{ height: "calc(100vh - 275px)", overflowY: "auto" }}>
        <MDBox px={1} sx={{ position: "relative" }}>
          <Stack
            py={1}
            sx={{
              position: "sticky",
              top: "0",
              background: "white",
              zIndex: "2",
            }}
          >
            <TextField
              placeholder="Search here"
              value={search}
              fullWidth
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </Stack>
          {isEdit.edit || addData ? (
            <MDBox
              sx={{
                padding: isEdit.disabled ? "1rem 0.5rem" : "0.5rem",
                borderBottom: `1px solid ${grey[400]}`,
                display: allClients.length === 0 ? "none" : "",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      clientConfig.length !== 0 &&
                      allClients.length === clientConfig.length
                    }
                    color="secondary"
                    onChange={(e) => {
                      let payload = [];
                      if (e.target.checked) {
                        payload = allClients.map((client) => Number(client.id));
                      } else {
                        payload = [];
                      }
                      setClientConfig(payload);
                    }}
                  />
                }
                label={"Select All"}
              />
            </MDBox>
          ) : (
            ""
          )}
          {allClients?.length > 0 ? (
            (search
              ? allClients.filter((client) =>
                  client.name?.toLowerCase().includes(search.toLowerCase())
                )
              : allClients
            )
              .sort((a, b) => {
                if (isEdit.edit || addData) {
                  // Simple alphabetical (case-insensitive) comparison without localeCompare
                  const nameA = a.name.toLowerCase();
                  const nameB = b.name.toLowerCase();

                  if (nameA < nameB) return -1;
                  if (nameA > nameB) return 1;
                  return a.name.localeCompare(b.name);
                }

                // clientConfig inclusion priority sorting
                const aIncluded = clientConfig.includes(Number(a.id)) ? -1 : 1;
                const bIncluded = clientConfig.includes(Number(b.id)) ? -1 : 1;
                if (aIncluded !== bIncluded) {
                  return aIncluded - bIncluded;
                }
                return a.name.localeCompare(b.name);
              })
              .map((client, i) => (
                <MDBox
                  sx={{
                    padding: isEdit.disabled ? "0.45rem 0.5rem" : "0.5rem",
                    borderBottom: `1px solid ${grey[400]}`,
                    cursor: "pointer",
                    background: selectedClient === client.id ? "#ffd1d6" : "",
                  }}
                  key={i}
                  onClick={() => {
                    if (!clientConfig.includes(Number(client.id))) {
                      return;
                    }
                    if (
                      isEdit.disabled &&
                      selectedClient !== Number(client.id)
                    ) {
                      setSelectedClient(client.id);
                    } else {
                      setSelectedClient(null);
                    }
                  }}
                >
                  <FormControlLabel
                    sx={{
                      opacity:
                        isEdit.edit ||
                        addData ||
                        clientConfig.includes(Number(client.id))
                          ? 1
                          : "0.7",
                    }}
                    disabled={isEdit.edit || addData ? false : true}
                    control={
                      <Checkbox
                        checked={clientConfig.includes(Number(client.id))}
                        color="secondary"
                        onChange={(e) => {
                          let payload = [...clientConfig];
                          if (e.target.checked) {
                            payload.push(Number(client.id));
                          } else {
                            let data = payload.filter(
                              (val) => val !== Number(client.id)
                            );
                            payload = data;
                          }
                          setClientConfig(payload);
                        }}
                      />
                    }
                    label={client.name}
                  />
                </MDBox>
              ))
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              mt={2}
            >
              <Typography
                fontSize="15px"
                color="dark"
                fontWeight="500"
                textAlign="center"
              >
                No clients here !
              </Typography>
            </Stack>
          )}
        </MDBox>
      </Stack>
    </MDBox>
  );
};

export default ClientConfig;
